import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { CurrencyPipe, DatePipe, SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { Skeleton } from 'primeng/skeleton';
import { CARE_PLAN_CLIENT_CONTEXT } from '../../care-plan-client-context.token';
import { CarePlanStore } from '../../care-plan.store';
import { OutcomeCard } from '../api/outcome-api.model';
import { OutcomeModalService } from '../outcome-modal.service';
import { OutcomesStore } from '../outcomes.store';

@Component({
  selector: 'bn-outcomes-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CurrencyPipe, DatePipe, SlicePipe, Skeleton, OverlayModule],
  providers: [OutcomesStore, DialogService, OutcomeModalService],
  templateUrl: './outcomes-section.component.html',
  styleUrl: './outcomes-section.component.scss',
})
export class OutcomesSectionComponent {
  readonly caseId = input.required<string>();
  readonly carePlan = input.required<CarePlanStore>();
  readonly store = inject(OutcomesStore);
  readonly detailPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
  ];
  private hideTimer?: ReturnType<typeof setTimeout>;
  readonly expanded = signal<number | null>(null);
  private readonly modal = inject(OutcomeModalService);
  readonly opening = this.modal.opening;
  readonly openError = this.modal.error;
  private readonly destroyRef = inject(DestroyRef);
  private readonly context = inject(CARE_PLAN_CLIENT_CONTEXT);

  constructor() {
    effect(() => {
      const caseId = this.caseId();
      const carePlan = this.carePlan();
      this.context.clientId();
      this.context.activeGuid();
      carePlan.activeGuid();
      untracked(() => {
        this.modal.reset();
        this.expanded.set(null);
        void this.store.bind(caseId, carePlan);
      });
    });
    this.destroyRef.onDestroy(() => {
      clearTimeout(this.hideTimer);
    });
  }

  showDetails(id: number): void {
    clearTimeout(this.hideTimer);
    this.expanded.set(id);
  }

  scheduleHideDetails(): void {
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => this.expanded.set(null), 100);
  }

  hideDetailsOnBlur(event: FocusEvent): void {
    if (
      !(event.currentTarget as HTMLElement).contains(
        event.relatedTarget as Node | null,
      )
    ) {
      this.expanded.set(null);
    }
  }

  async openEditor(outcome?: OutcomeCard): Promise<void> {
    this.expanded.set(null);
    await this.modal.open(this.store, outcome);
  }
}
